import axiosInstance from "../../../helpers/api";

/** OCR and mark-scheme registration always use this model. */
export const FIXED_GEMINI_FLASH = "gemini-2.5-flash";

const DOCUMENTED_MODELS = [
  { id: "gemini-2.5-flash", provider: "gemini", label: "Gemini 2.5 Flash", vision: true },
  { id: "gemini-2.5-pro", provider: "gemini", label: "Gemini 2.5 Pro", vision: true },
  { id: "gemini-2.5-flash-lite", provider: "gemini", label: "Gemini 2.5 Flash-Lite", vision: true },
  { id: "gpt-4o-mini", provider: "openai", label: "GPT-4o mini", vision: true },
  { id: "gpt-4o", provider: "openai", label: "GPT-4o", vision: true },
  { id: "gpt-4.1-mini", provider: "openai", label: "GPT-4.1 mini", vision: true },
  { id: "gpt-4.1", provider: "openai", label: "GPT-4.1", vision: true },
  { id: "gpt-5.6-luna", provider: "openai", label: "GPT-5.6 Luna", vision: true },
  { id: "gpt-5.6-terra", provider: "openai", label: "GPT-5.6 Terra", vision: true },
  { id: "gpt-5.6-sol", provider: "openai", label: "GPT-5.6 Sol", vision: true },
];

const PROVIDER_LABELS = {
  gemini: "Gemini",
  openai: "OpenAI",
};

let catalogPromise = null;
let rememberedGradingModel = null;

export function fallbackCatalog() {
  return {
    default_ocr_model: FIXED_GEMINI_FLASH,
    default_grading_model: FIXED_GEMINI_FLASH,
    providers: {
      gemini: { configured: true },
      openai: { configured: true },
    },
    models: DOCUMENTED_MODELS,
  };
}

function normalizeCatalog(raw) {
  const nested = raw?.data;
  const data = nested && Array.isArray(nested.models) ? nested : raw;
  if (!data || !Array.isArray(data.models) || !data.models.length) {
    return fallbackCatalog();
  }
  return data;
}

function providerLabel(provider) {
  if (PROVIDER_LABELS[provider]) return PROVIDER_LABELS[provider];
  if (!provider) return "Other";
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

export function toGradingModelGroups(catalog) {
  const providers = catalog?.providers || {};
  const models = Array.isArray(catalog?.models) ? catalog.models : [];
  const byProvider = new Map();

  models.forEach((model) => {
    if (!model?.id) return;
    const provider = model.provider || "other";
    const configured = providers[provider]?.configured !== false;
    if (!byProvider.has(provider)) byProvider.set(provider, []);
    byProvider.get(provider).push({
      value: model.id,
      label: model.label || model.id,
      isDisabled: !configured,
    });
  });

  const preferredOrder = ["gemini", "openai"];
  const keys = [
    ...preferredOrder.filter((key) => byProvider.has(key)),
    ...[...byProvider.keys()].filter((key) => !preferredOrder.includes(key)),
  ];

  return keys.map((provider) => ({
    label: providerLabel(provider),
    options: byProvider.get(provider),
  }));
}

function enabledModelIds(groups) {
  return groups.flatMap((group) =>
    (group.options || []).filter((option) => !option.isDisabled).map((option) => option.value)
  );
}

export function resolveGradingModel(catalog, groups, preferred) {
  const enabled = enabledModelIds(groups);
  if (preferred && enabled.includes(preferred)) return preferred;
  const fallback = catalog?.default_grading_model || FIXED_GEMINI_FLASH;
  if (enabled.includes(fallback)) return fallback;
  return enabled[0] || FIXED_GEMINI_FLASH;
}

export function findGroupedOption(groups, value) {
  for (const group of groups || []) {
    const match = (group.options || []).find((option) => option.value === value);
    if (match) return match;
  }
  return null;
}

export function rememberGradingModel(modelId) {
  rememberedGradingModel = modelId || null;
}

export function rememberedGradingModelId() {
  return rememberedGradingModel;
}

async function fetchCatalog() {
  const response = await axiosInstance.get("/admin/mark_schemes/models");
  return normalizeCatalog(response.data);
}

/** Loads the model catalog through Laravel. Falls back to the documented catalog if the request fails. */
export function loadGraderModelCatalog() {
  if (!catalogPromise) {
    catalogPromise = fetchCatalog().catch(() => {
      catalogPromise = null;
      return fallbackCatalog();
    });
  }
  return catalogPromise;
}

export function buildMarkPaperBody({ markSchemeId, url, pages, gradingModel }) {
  const body = {
    mark_scheme_id: markSchemeId,
    url,
    ocr_model: FIXED_GEMINI_FLASH,
    grading_model: gradingModel || FIXED_GEMINI_FLASH,
  };
  if (pages) body.pages = pages;
  return body;
}
